import React, { useState, useEffect } from 'react'
import { Alert, Box, Button, Stack, ResponsiveStack, Input, Heading, Text, Select, ImageLogo } from '@/components/lib'
import api from '../../api'
import useStore from '../../store'
import utils from '../../utils/utils.js'
import FileChooser from '../includes/FileChooser'
import QrCode from '../includes/QrCode'
import ColourChooser from '../includes/ColourChooser'

export default function TeamBranding () {
  const [name, setName] = useState('')
  const { user, team, updateTeam } = useStore()

  useEffect(() => {
    if (team) {
      setName(team.name)
    }
  }, [team])

  function saveName () {
    api.teams.update(team._id, { name }, team => {
      updateTeam(team)
      utils.toast('success', 'Team updated')
    }, err => utils.toast('error', 'Unable to update team', err.message))
  }

  function updateLogo ({ storedName }) {
    api.teams.update(team._id, { logo: storedName }, team => {
      if (!storedName) {
        team.logo = null
        team.logoUrl = null
      }
      updateTeam(team)
      utils.toast('success', 'Team updated')
    }, err => utils.toast('error', 'Unable to update team', err.message))
  }

  function updateDarkLogo ({ storedName }) {
    api.teams.update(team._id, { darkLogo: storedName }, team => {
      if (!storedName) {
        team.darkLogo = null
        team.darkLogoUrl = null
      }
      updateTeam(team)
      utils.toast('success', 'Team updated')
    }, err => utils.toast('error', 'Unable to update team', err.message))
  }

  function updateQrIcon ({ storedName }) {
    api.teams.update(team._id, { qrIcon: storedName }, team => {
      if (!storedName) {
        team.qrIcon = null
        team.qrIconUrl = null
      }
      updateTeam(team)
      utils.toast('success', 'Team updated')
    }, err => utils.toast('error', 'Unable to update team', err.message))
  }

  function updateQrValue (valName, value) {
    api.teams.update(team._id, { [valName]: value }, team => {
      updateTeam(team)
    }, err => utils.toast('error', 'Unable to update team', err.message))
  }

  const canEdit = utils.hasPermission(user, 'team.manage', user.team)

  return (
    <Stack gap={5}>
      {utils.hasPermission(user, 'team.manage', team?._id)
        ? (
          <>
            <Stack direction={['column', 'row']} gap={5}>
              <Stack p={3} rounded='md' borderWidth='1px' gap={5}>
                <Box>
                  <Heading size='md'>Team identity</Heading>
                  <Text color='fg.muted'>Choose how other users in {team?.name} and your trial participants see your team.</Text>
                </Box>

                <Stack>
                  <Heading size='sm'>Team name</Heading>
                  <Text color='fg.muted'>Change the name of your team. This name will be shown to participants of your trials.</Text>
                  <Input readOnly={!canEdit} value={name} onChange={e => setName(e.target.value)} />
                  {canEdit &&
                    <Box><Button colorPalette='primary' onClick={saveName}>Save name</Button></Box>}
                </Stack>
              </Stack>

              <Stack p={3} rounded='md' borderWidth='1px' gap={5}>
                <Box>
                  <Heading size='md'>Logo</Heading>
                  <Text color='fg.muted'>Select logos that represent your team. These will display for all users of your team and in some circumstances to participants.</Text>
                </Box>
                <Stack direction='row' gap={3}>
                  <Stack w='200px'>
                    <Heading size='sm'>Team logo</Heading>
                    <Text color='fg.muted'>Add a logo to your team.</Text>
                  </Stack>
                  <Stack flex='1' gap={3}>
                    {team.logoUrl
                      ? <ImageLogo src={team.logoUrl} w={200} />
                      : <Alert variant='left-accent'>No logo is currently set.</Alert>}
                    <Stack direction='row'>
                      <FileChooser
                        accept='image/*'
                        forType='team' forObject={team}
                        onComplete={updateLogo}
                        trigger={<Button colorPalette='primary'>Select an image</Button>}
                      />
                      <Button onClick={e => updateLogo({ storedName: null })} variant='ghost'>Remove</Button>
                    </Stack>
                  </Stack>
                </Stack>

                <Stack direction='row' gap={3}>
                  <Stack w='200px'>
                    <Heading size='sm'>Dark mode logo</Heading>
                    <Text color='fg.muted'>Upload a version of your logo optimised for dark backgrounds. This will be used when dark mode is enabled.</Text>
                  </Stack>
                  <Stack flex='1' gap={3}>
                    {team.darkLogoUrl
                      ? <ImageLogo src={team.darkLogoUrl} w={200} isDark />
                      : <Alert variant='left-accent'>No dark mode logo is currently set.</Alert>}
                    <Stack direction='row'>
                      <FileChooser
                        accept='image/*'
                        forType='team' forObject={team}
                        onComplete={updateDarkLogo}
                        trigger={<Button colorPalette='primary'>Select an image</Button>}
                      />
                      <Button onClick={e => updateDarkLogo({ storedName: null })} variant='ghost'>Remove</Button>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>

            <Stack p={3} rounded='md' borderWidth='1px'>
              <Box>
                <Heading size='md'>QR code branding</Heading>
                <Text color='fg.muted'>Customise the appearance of QR codes generated within Trialflare.</Text>
              </Box>
              <ResponsiveStack gap={5}>
                <Box borderWidth='1px' borderColor='border.default' p={3} rounded='md'>
                  <Heading size='sm'>Preview</Heading>
                  <QrCode name='trialflare' url='https://trialflare.com' size={200} />
                </Box>
                <Stack gap={3}>
                  <Heading size='sm'>Icon</Heading>
                  <Text color='fg.muted'>Add an icon to the center of the QR code.</Text>
                  <FileChooser
                    accept='image/*'
                    forType='team' forObject={team}
                    onComplete={updateQrIcon}
                    trigger={<Button colorPalette='primary' size='sm'>Update icon</Button>}
                  />
                  {team.qrIconUrl &&
                    <Button onClick={e => updateQrIcon({ storedName: null })} size='sm'>Remove icon</Button>}

                  <Heading size='sm'>Style</Heading>
                  <Select value={team.qrDotStyle} onChange={e => updateQrValue('qrDotStyle', e.target.value)}>
                    <option value='squares'>Squares</option>
                    <option value='dots'>Dots</option>
                  </Select>

                  <Heading size='sm'>Corner style</Heading>
                  <Select value={team.qrCornerSquareStyle} onChange={e => updateQrValue('qrCornerSquareStyle', e.target.value)}>
                    <option value='dot'>Dot</option>
                    <option value='extra-rounded'>Extra rounded</option>
                    <option value='square'>Square</option>
                  </Select>
                </Stack>

                <Stack gap={3}>
                  <Heading size='sm'>Background</Heading>
                  <Text color='fg.muted'>Choose a background colour for the QR code.</Text>
                  <ColourChooser value={team.qrBackgroundColour || '#FFFFEF'} onChange={value => updateQrValue('qrBackgroundColour', value)} />
                  {team.qrBackgroundColour &&
                    <Button onClick={e => updateQrValue('qrBackgroundColour', null)} size='sm'>Remove background colour</Button>}

                  <Heading size='sm'>Foreground</Heading>
                  <Text color='fg.muted'>Choose a colour for the QR code.</Text>
                  <ColourChooser value={team.qrColour || '#805AD5'} onChange={value => updateQrValue('qrColour', value)} />
                  {team.qrColour &&
                    <Button onClick={e => updateQrValue('qrColour', null)} size='sm'>Remove foreground colour</Button>}
                </Stack>
              </ResponsiveStack>
            </Stack>

          </>)
        : (
          <Box>
            <Heading>Page unavailable</Heading>
            <Text>This page is only viewable by users that manage the team</Text>
          </Box>)}
    </Stack>
  )
}
